import {
  BaseMutationOptions,
  MutationResult,
  parser,
  useMutation as apolloUseMutation
} from '@apollo/client'
import { DocumentNode } from '@apollo/client'
import { ToolBox } from 'app/lib/hooks/toolBox'
import { RecordTypeEnum } from 'app/queries'

import { applyNormalizer, useNormalizer } from '../apollo/normalizers/utilities'
import { notify } from '../Notify'
import { useErrorHandler } from './useQuery'

interface Result<T> extends Omit<MutationResult<T>, 'data'> {
  data: T
  execute: (payload: unknown) => Promise<T>
}

export interface MutationOptions extends BaseMutationOptions {
  silentSuccess?: boolean
  recordType?: RecordTypeEnum
  action?: 'update' | 'destroy' | 'create' | string
  normalizer?: (data: unknown) => unknown
  payloadNormalizer?: (data: unknown) => unknown
}

interface DestroyOptions extends MutationOptions {
  destroyFromCacheStore?: boolean
}

export const useMutation = <T>(query: DocumentNode, options: MutationOptions = {}): Result<T> => {
  const {
    silentSuccess,
    recordType,
    payloadNormalizer,
    normalizer,
    action = 'update',
    ...otherOptions
  } = options
  const { onError } = useErrorHandler(options.onError)
  const [executeMutation, { data, loading, ...others }] = apolloUseMutation(query, {
    ...otherOptions
  })
  const { smartTranslation } = ToolBox.useString()
  const { normalizedData } = useNormalizer(data, normalizer)

  const execute = (payload: unknown) => {
    const input = payloadNormalizer ? payloadNormalizer(payload) : payload

    return executeMutation({ variables: { input } })
      .then(({ data, errors: apiErrors }) => {
        const queryName = parser(query).name
        const errors = [...(apiErrors || []), ...(data?.[queryName]?.errors || [])]

        if (errors.length) {
          throw new Error(errors[0].message)
        } else if (!silentSuccess) {
          notify({
            message: smartTranslation({
              id: `form.${recordType}.message.successful.${action}`,
              recordType: recordType
            }),
            intent: 'success'
          })
        }
        return applyNormalizer(data?.[queryName], normalizer)
      })
      .catch((error) => {
        onError(error)
        throw error
      })
  }

  return {
    data: normalizedData,
    loading,
    execute,
    ...others
  }
}

export const useDestroy = <T>(query: DocumentNode, options: DestroyOptions = {}): Result<T> => {
  const { destroyFromCacheStore = true, recordType, ...otherOptions } = options

  const props = useMutation<T>(query, {
    action: 'destroy',
    update: (cache, { data }) => {
      if (destroyFromCacheStore) {
        const queryName = parser(query).name
        cache.evict({ id: cache.identify(data[queryName]?.[recordType]) })
      }
    },
    recordType,
    ...otherOptions
  })

  return props
}
